import { create } from 'zustand';
import { NexoyaProvider } from '../types';

export type AdAccountFilterItem = {
  contentId: number;
  title: string;
  providerId: number;
};

export interface ProviderFilterState {
  providersFilter: NexoyaProvider[];
  adAccountsFilter: AdAccountFilterItem[];

  handleAddProvider: (provider: NexoyaProvider) => void;
  handleRemoveProvider: (provider: NexoyaProvider) => void;

  handleAddAdAccount: (adAccount: AdAccountFilterItem) => void;
  handleRemoveAdAccount: (adAccount: AdAccountFilterItem) => void;
  handleAddAllAdAccountsForProvider: (providerId: number, adAccounts: AdAccountFilterItem[]) => void;
  handleRemoveAllAdAccountsForProvider: (providerId: number) => void;

  handleSelectChannel: (provider: NexoyaProvider, adAccounts: AdAccountFilterItem[]) => void;
  handleDeselectChannel: (provider: NexoyaProvider) => void;

  resetProviderFilters: (type?: 'providers' | 'adAccounts') => void;
}

export const useProviderFilterStore = create<ProviderFilterState>((set) => ({
  providersFilter: [],
  adAccountsFilter: [],

  handleAddProvider: (provider) =>
    set((state) => {
      if (state.providersFilter.some((p) => p.provider_id === provider.provider_id)) return state;
      return { providersFilter: [...state.providersFilter, provider] };
    }),

  handleRemoveProvider: (provider) =>
    set((state) => ({
      providersFilter: state.providersFilter.filter((p) => p.provider_id !== provider.provider_id),
    })),

  handleAddAdAccount: (adAccount) =>
    set((state) => {
      if (state.adAccountsFilter.some((a) => a.contentId === adAccount.contentId)) return state;
      return { adAccountsFilter: [...state.adAccountsFilter, adAccount] };
    }),

  handleRemoveAdAccount: (adAccount) =>
    set((state) => ({
      adAccountsFilter: state.adAccountsFilter.filter((a) => a.contentId !== adAccount.contentId),
    })),

  handleAddAllAdAccountsForProvider: (_providerId, adAccounts) =>
    set((state) => {
      const existingIds = new Set(state.adAccountsFilter.map((a) => a.contentId));
      const newAccounts = adAccounts.filter((a) => !existingIds.has(a.contentId));
      return { adAccountsFilter: [...state.adAccountsFilter, ...newAccounts] };
    }),

  handleRemoveAllAdAccountsForProvider: (providerId) =>
    set((state) => ({
      adAccountsFilter: state.adAccountsFilter.filter((a) => a.providerId !== providerId),
    })),

  handleSelectChannel: (provider, adAccounts) =>
    set((state) => {
      const existingIds = new Set(state.adAccountsFilter.map((a) => a.contentId));
      const newAccounts = adAccounts.filter((a) => !existingIds.has(a.contentId));
      const hasProvider = state.providersFilter.some((p) => p.provider_id === provider.provider_id);
      return {
        adAccountsFilter: [...state.adAccountsFilter, ...newAccounts],
        providersFilter: hasProvider ? state.providersFilter : [...state.providersFilter, provider],
      };
    }),

  handleDeselectChannel: (provider) =>
    set((state) => ({
      adAccountsFilter: state.adAccountsFilter.filter((a) => a.providerId !== provider.provider_id),
      providersFilter: state.providersFilter.filter((p) => p.provider_id !== provider.provider_id),
    })),

  resetProviderFilters: (type) =>
    set(() => {
      if (!type) return { providersFilter: [], adAccountsFilter: [] };
      const resetMap = {
        providers: { providersFilter: [] as NexoyaProvider[] },
        adAccounts: { adAccountsFilter: [] as AdAccountFilterItem[] },
      };
      return resetMap[type] || {};
    }),
}));
